import styled from '@emotion/styled';
import type { Prismic } from '@landsbankinn/prismic';
import {
  Divider,
  device,
  typography,
  useMedia,
  container,
  inViewConfig,
} from '@landsbankinn/ui';
import React, { Children } from 'react';

import { useInView } from 'react-intersection-observer';
import { useSpring, animated } from 'react-spring';
import { Img } from '../../components/img/Img';

export interface HeroProps {
  title?: React.ReactNode;
  description?: string;
  image?: Prismic.Image;
  actions?: React.ReactNode;
  isAnimated?: boolean;
}

const isEmpty = (value: string | React.ReactNode) => {
  if (Children.count(value) === 0) {
    return true;
  }
  return String(value ?? '').trim() === '';
};

const Host = styled.div<{
  hasButton: boolean;
}>`
  position: relative;
  padding-bottom: 1px;
  margin-bottom: ${(props) => (props.hasButton ? '116px' : '72px')};
  background: ${(props) => props.theme.shades.ternary2};

  @media ${device.tablet} {
    padding-top: 105px;
    margin-top: -86px;
    margin-bottom: 116px;
  }
  @media (min-width: 950px) {
    padding-top: 86px;
  }
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: -50px;
  background: ${(props) => props.theme.shades.ternary2};
`;

const Container = styled(animated.div)`
  @media ${device.tablet} {
    ${container}

    margin-top: -20px;
  }
`;

const Image = styled.img`
  object-fit: cover;
`.withComponent(Img);

const AspectRatio = styled.div<HeroProps>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;

  margin-bottom: -50px;

  @media ${device.tablet} {
    flex-direction: row-reverse;

    &:before {
      padding-bottom: ${() => 0.49 * 100}%;
      content: '';
      float: left;
    }

    &:after {
      display: table;
      content: '';
      clear: both;
    }
  }
`;

const Title = styled.h1`
  ${typography.h2}

  @media ${device.tablet} {
    ${typography.h3}
  }

  @media ${device.desktop} {
    ${typography.h2}
    margin-bottom: 35px;
  }
`;

const Description = styled.div`
  @media ${device.desktop} {
    margin-top: 35px;
  }
`;

const ImageAspect = styled.div`
  width: 100%;

  &:before {
    content: '';
    display: block;
    padding-top: ${1 * 100}%;

    @media ${device.tablet} {
      display: block;
    }

    @media ${device.desktop} {
      display: block;
      padding-top: ${(3 / 4) * 100}%;
    }
  }

  > :first-child {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  }

  @media ${device.tablet} {
    position: absolute;
    bottom: -31px;
  }
`;

const Content = styled.div<HeroProps>`
  position: relative;
  padding: 34px 34px 30px;

  display: flex;
  flex-direction: column;

  @media ${device.mobile} {
    padding: 34px 44px 30px;
  }

  @media ${device.tablet} {
    padding: 63px 30px 63px 0;
    padding-top: 105px;
    background-color: transparent;
    width: calc(50%);
  }

  @media ${device.desktop} {
    width: calc(30% + 53px);
  }
`;

const Copy = styled(animated.div)`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-bottom: 56px;

  @media ${device.tablet} {
    padding-bottom: 36px;
  }
`;

const Actions = styled(animated.div)`
  margin-bottom: -60px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  > * {
    margin-top: 20px;
    width: 100%;
  }

  > *:first-child {
    margin-top: 0;
  }

  @media ${device.tablet} {
    margin-bottom: -94px;

    > * {
      margin-top: 30px;
      width: 183px;
    }
  }
`;

const Column = styled.div`
  position: relative;
  width: 100%;

  @media ${device.tablet} {
    width: calc(50%);
  }

  @media ${device.desktop} {
    width: calc(70% - 30px);
  }
`;

export function ArticlePageHero(props: HeroProps) {
  const [ref, inView] = useInView(inViewConfig);

  const isDesktop = useMedia('lg');

  const { isAnimated, image, title, actions, description } = props;

  const hasButton = !!actions;
  const visible = isAnimated ?? inView;
  const delay = 0;

  const copyStyle = useSpring({
    opacity: visible ? 1 : 0,
    y: visible ? 0 : 20,
    delay,
  });

  const actionsStyle = useSpring({
    opacity: visible ? 1 : 0,
    overflow: 'hidden',
    delay: delay + 100,
    config: {
      tension: 320,
    },
  });

  const imageStyle = useSpring({
    x: visible ? '0%' : '101%',
  });

  return (
    <Host ref={ref} hasButton={hasButton} id="hero">
      <Background />
      <Container>
        <AspectRatio>
          <Column>
            <ImageAspect>
              <animated.div
                style={{
                  overflow: 'hidden',
                  transform: imageStyle.x.to((v) => `translateX(-${v})`),
                }}
              >
                <Image
                  src={image}
                  ar={isDesktop ? 'ar_4_3' : 'ar_1_1'}
                  isFit={true}
                  containerStyle={imageStyle}
                  percentage={0.7}
                />
              </animated.div>
            </ImageAspect>
          </Column>

          <Content>
            <Copy style={copyStyle}>
              {!isEmpty(title) && <Title>{title}</Title>}
              {!isEmpty(description) && (
                <>
                  <Divider />
                  <Description>
                    {/* {description} */}
                    {description?.slice(0, 130)}...
                  </Description>
                </>
              )}
            </Copy>

            {!isEmpty(actions) && (
              <Actions style={actionsStyle}>{actions}</Actions>
            )}
          </Content>
        </AspectRatio>
      </Container>
    </Host>
  );
}
